import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Components/ui/tabs';
import { Separator } from '@/Components/ui/separator';
import { Stack } from '@/Components/ui/layout';
import { Inline } from '@/Components/ui/layout';
import { Heading } from '@/Components/ui/typography';
import { Text } from '@/Components/ui/typography';
import { X, CreditCard, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const Checkout = ({ plan, onClose, open = true }) => {
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [selectedCard, setSelectedCard] = useState('new');

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[950px] p-0 overflow-hidden">
        <DialogHeader className="flex flex-row items-center justify-between p-5 bg-[#f8f9fa] border-b">
          <DialogTitle className="text-2xl font-medium text-foreground m-0">
            Checkout
          </DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close checkout"
            className="w-9 h-9 rounded-full bg-[#f8f9fa] hover:bg-[#dfe3e8]"
          >
            <X className="w-5 h-5" />
          </Button>
        </DialogHeader>

        <div className="p-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Payment Section */}
            <Card className="flex-1 max-w-[400px]">
              <CardHeader>
                <CardTitle className="text-xl font-medium mb-4 pb-2 border-b">
                  Payment System
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Tabs value={paymentMethod} onValueChange={setPaymentMethod}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="card" className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      <span>Debit/Credit Card</span>
                    </TabsTrigger>
                    <TabsTrigger value="paypal" className="flex items-center gap-2">
                      <img
                        src="https://cdn.builder.io/api/v1/image/assets/TEMP/3d6db7f5d4d91615acabcc272438407cd3abf47f9945e8eb9db52b6058719163?placeholderIfAbsent=true&apiKey=011132b2f41c4af7b38e22acc88aa803"
                        alt="PayPal"
                        className="w-5 h-5"
                      />
                      <span>Paypal</span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="card" className="space-y-4 mt-4">
                    <div className="space-y-3 border-t pt-4">
                      <label className="flex items-center gap-4 p-3 border rounded-lg cursor-pointer hover:border-primary transition-colors">
                        <input
                          type="radio"
                          name="payment"
                          value="saved"
                          checked={selectedCard === 'saved'}
                          onChange={() => setSelectedCard('saved')}
                          className="w-4 h-4"
                        />
                        <img
                          src="https://cdn.builder.io/api/v1/image/assets/TEMP/d92eb74efd56a43568f5d8732c501dd69d749562d5fe89b3560f1a9110045f3c?placeholderIfAbsent=true&apiKey=011132b2f41c4af7b38e22acc88aa803"
                          alt="Card"
                          className="w-8 h-8"
                        />
                        <div className="flex-1">
                          <div className="flex flex-col gap-1">
                            <Text size="xs" className="text-muted-foreground">Card Number</Text>
                            <Text size="sm" className="font-medium">5847 **** **** ****</Text>
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-col gap-1">
                            <Text size="xs" className="text-muted-foreground">Name on Card</Text>
                            <Text size="sm" className="font-medium">Esther Howard</Text>
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center gap-4 p-3 border rounded-lg cursor-pointer hover:border-primary transition-colors">
                        <input
                          type="radio"
                          name="payment"
                          value="new"
                          checked={selectedCard === 'new'}
                          onChange={() => setSelectedCard('new')}
                          className="w-4 h-4"
                        />
                        <Text size="sm" className="font-medium">New payment card</Text>
                      </label>
                    </div>

                    {selectedCard === 'new' && (
                      <form className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label htmlFor="card-name">Name on Card</Label>
                          <Input id="card-name" type="text" placeholder="Name" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="card-number">Credit Card</Label>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-3 flex-1 border rounded-md px-3 py-2">
                              <img
                                src="https://cdn.builder.io/api/v1/image/assets/TEMP/56c4c0cf24619173cc89f088249594cc72697680ab55eaabd7e17f3f9341ea28?placeholderIfAbsent=true&apiKey=011132b2f41c4af7b38e22acc88aa803"
                                alt="Card"
                                className="w-8 h-8"
                              />
                              <Input
                                id="card-number"
                                type="text"
                                placeholder="Card number"
                                className="border-0 p-0 focus-visible:ring-0"
                              />
                            </div>
                            <div className="flex gap-3">
                              <Input type="text" placeholder="MM/YY" className="w-20" />
                              <Input type="text" placeholder="CVC" className="w-20" />
                            </div>
                          </div>
                        </div>
                      </form>
                    )}
                  </TabsContent>

                  <TabsContent value="paypal" className="mt-4">
                    <Text size="sm" className="text-muted-foreground">
                      PayPal payment integration coming soon.
                    </Text>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Order Summary Section */}
            <Card className="flex-1 max-w-[500px]">
              <CardHeader>
                <CardTitle className="text-xl font-medium mb-4 pb-2 border-b">
                  Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-[#f8f9fa] rounded-lg p-5 border space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Text size="sm" className="text-muted-foreground">
                        Pricing Plan:{' '}
                        <span className="text-foreground font-medium">{plan.title}</span>
                      </Text>
                      <Text size="base" className="font-semibold">
                        ${plan.price}.00
                      </Text>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <Text size="base" className="font-medium">Total:</Text>
                      <Text size="lg" className="font-semibold">
                        ${plan.price} USD
                      </Text>
                    </div>
                  </div>
                  <Button className="w-full bg-[#1a73e8] hover:bg-[#145ab6] text-white">
                    <span>Choose Plan</span>
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Text size="xs" className="text-center text-muted-foreground block">
                    This package will expire after one month.
                  </Text>
                </div>
              </CardContent>
            </Card>
          </div>

          {selectedCard === 'new' && (
            <div className="mt-6 flex justify-end">
              <Button className="bg-[#1a73e8] hover:bg-[#145ab6] text-white">
                Proceed
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Checkout;
